import React, { useState } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Box,
  Button,
  Stack,
  Divider,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
  Collapse,
  Fade
} from '@mui/material';
import {
  Image as ImageIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { exportDeck } from '../services/decks';
import defaultDeckImage from '../assets/default-deck.png';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:3000';

function DeckSettings({ deck, onUpdate }) {
  const [settings, setSettings] = useState({
    name: deck?.name || '',
    description: deck?.description || '',
    cardWidth: deck?.cardWidth || 500,
    cardHeight: deck?.cardHeight || 300,
    frontFontSize: deck?.frontFontSize || 24,
    backFontSize: deck?.backFontSize || 24,
    studyMode: deck?.studyMode || 'random'
  });
  const [isExpanded, setIsExpanded] = useState(false);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    onUpdate({ ...deck, [field]: value });
  };

  const handleDimensionChange = (field) => (event) => {
    const value = parseInt(event.target.value, 10);
    if (value > 0) {
      setSettings(prev => ({
        ...prev,
        [field]: value
      }));
      onUpdate({ ...deck, [field]: value });
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('image', file);
      // Copier les paramètres actuels
      formData.append('name', settings.name);
      formData.append('description', settings.description);
      formData.append('cardWidth', settings.cardWidth);
      formData.append('cardHeight', settings.cardHeight);
      formData.append('frontFontSize', settings.frontFontSize);
      formData.append('backFontSize', settings.backFontSize);
      formData.append('studyMode', settings.studyMode);
      onUpdate(formData);
    }
  };

  const handleStudyModeChange = (event, newMode) => {
    if (newMode !== null) {
      // Créer un FormData avec toutes les données actuelles
      const formData = new FormData();
      formData.append('name', settings.name);
      formData.append('description', settings.description);
      formData.append('cardWidth', settings.cardWidth);
      formData.append('cardHeight', settings.cardHeight);
      formData.append('frontFontSize', settings.frontFontSize);
      formData.append('backFontSize', settings.backFontSize);
      formData.append('studyMode', newMode);

      setSettings(prev => ({
        ...prev,
        studyMode: newMode
      }));
      
      // Envoyer toutes les données au serveur
      onUpdate(formData);
    }
  };

  const handleExport = async () => {
    try {
      await exportDeck(deck.id);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
    }
  };

  return (
    <Paper sx={{ 
      p: 3, 
      mb: 3,
      transition: 'padding 300ms ease-in-out',
      ...((!isExpanded) && {
        pb: 1
      })
    }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          mb: 2,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover:not(:has(.export-button:hover)) .expand-icon': {
            backgroundColor: 'action.hover'
          }
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="h5">
          Paramètres du paquet
        </Typography>
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1 
          }}
        >
          <Box onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Exporter en JSON">
              <IconButton 
                onClick={handleExport} 
                size="small"
                className="export-button"
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Tooltip title={isExpanded ? "Réduire" : "Agrandir"}>
            <IconButton 
              size="small"
              className="expand-icon"
              sx={{ 
                transition: 'background-color 0.2s'
              }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        <Fade in={isExpanded} timeout={{ enter: 700, exit: 300 }}>
          <Stack spacing={3} sx={{
            opacity: isExpanded ? 1 : 0,
            transition: 'opacity 300ms ease-in-out'
          }}>
            <Divider />
            
            <Box sx={{ textAlign: 'center' }}>
              <Box
                component="img"
                src={deck.imageUrl ? `${API_BASE_URL}${deck.imageUrl}` : defaultDeckImage}
                alt="Image du paquet"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '200px',
                  objectFit: 'contain',
                  mb: 2,
                  borderRadius: 1,
                  opacity: deck.imageUrl ? 1 : 0.7
                }}
              />
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                id="deck-image"
                onChange={handleImageChange}
              />
              <label htmlFor="deck-image">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<ImageIcon />}
                  fullWidth
                >
                  {deck.imageUrl ? 'Changer l\'image' : 'Ajouter une image'}
                </Button>
              </label>
            </Box>

            <Divider />

            <TextField
              label="Nom du paquet"
              value={settings.name}
              onChange={handleChange('name')}
              fullWidth
              variant="outlined"
            />

            <TextField
              label="Description"
              value={settings.description}
              onChange={handleChange('description')}
              multiline
              rows={3}
              fullWidth
              variant="outlined"
            />

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Mode d'étude
              </Typography>
              <ToggleButtonGroup
                value={settings.studyMode}
                exclusive
                onChange={handleStudyModeChange}
                fullWidth
                sx={{ mt: 1 }}
              >
                <ToggleButton 
                  value="front" 
                  sx={{ 
                    py: 1,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      }
                    }
                  }}
                >
                  Recto
                </ToggleButton>
                <ToggleButton 
                  value="back"
                  sx={{ 
                    py: 1,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      }
                    }
                  }}
                >
                  Verso
                </ToggleButton>
                <ToggleButton 
                  value="random"
                  sx={{ 
                    py: 1,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      }
                    }
                  }}
                >
                  Aléatoire
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Typography variant="subtitle1" gutterBottom>
              Dimensions des cartes
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Largeur"
                value={settings.cardWidth}
                onChange={handleDimensionChange('cardWidth')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">px</InputAdornment>,
                  inputProps: { min: 200, max: 1000 }
                }}
              />
              <TextField
                fullWidth
                type="number"
                label="Hauteur"
                value={settings.cardHeight}
                onChange={handleDimensionChange('cardHeight')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">px</InputAdornment>,
                  inputProps: { min: 200, max: 1000 }
                }}
              />
            </Box>

            <Typography variant="subtitle1" gutterBottom>
              Taille des textes
            </Typography>
            <TextField
              fullWidth
              type="number"
              label="Recto"
              value={settings.frontFontSize}
              onChange={handleDimensionChange('frontFontSize')}
              InputProps={{
                endAdornment: <InputAdornment position="end">px</InputAdornment>,
                inputProps: { min: 12, max: 72 }
              }}
            />
            <TextField
              fullWidth
              type="number"
              label="Verso"
              value={settings.backFontSize}
              onChange={handleDimensionChange('backFontSize')}
              InputProps={{
                endAdornment: <InputAdornment position="end">px</InputAdornment>,
                inputProps: { min: 12, max: 72 }
              }}
            />
          </Stack>
        </Fade>
      </Collapse>
    </Paper>
  );
}

export default DeckSettings; 